import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { User, Package, MapPin, Phone, Mail, Loader2, ChevronDown, ChevronUp, Save, Edit3, ShoppingBag, Calendar, CreditCard } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FloatingCart from "@/components/layout/FloatingCart";
import FloatingWhatsApp from "@/components/layout/FloatingWhatsApp";
import SEOHead from "@/components/SEOHead";
import { useLanguage } from "@/context/LanguageContext";
import { format } from "date-fns";

interface ProfileData {
  full_name: string;
  phone: string;
  address: string;
  created_at: string;
}

interface Order {
  id: string;
  total_amount: number;
  status: string;
  created_at: string;
  shipping_city: string | null;
  shipping_fee: number;
  items: { product_name: string; quantity: number; unit_price: number; size: string | null }[];
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/10 text-yellow-600",
  confirmed: "bg-blue-500/10 text-blue-600",
  processing: "bg-purple-500/10 text-purple-600",
  shipped: "bg-indigo-500/10 text-indigo-600",
  delivered: "bg-green-500/10 text-green-600",
  cancelled: "bg-red-500/10 text-red-600",
};

const Profile = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [profile, setProfile] = useState<ProfileData>({ full_name: "", phone: "", address: "", created_at: "" });
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [profileRes, ordersRes] = await Promise.all([
        supabase.from("profiles").select("full_name, phone, address, created_at").eq("user_id", user.id).single(),
        supabase.from("orders").select("id, total_amount, status, created_at, shipping_city, shipping_fee").eq("user_id", user.id).order("created_at", { ascending: false }),
      ]);

      if (profileRes.data) {
        setProfile({
          full_name: profileRes.data.full_name || "",
          phone: profileRes.data.phone || "",
          address: profileRes.data.address || "",
          created_at: profileRes.data.created_at || "",
        });
      }

      if (ordersRes.data) {
        const orderIds = ordersRes.data.map((o) => o.id);
        const { data: items } = orderIds.length > 0
          ? await supabase.from("order_items").select("order_id, product_name, quantity, unit_price, size").in("order_id", orderIds)
          : { data: [] };

        const ordersWithItems = ordersRes.data.map((o) => ({
          ...o,
          items: items?.filter((i) => i.order_id === o.id) || [],
        }));
        setOrders(ordersWithItems);
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: profile.full_name || null,
          phone: profile.phone || null,
          address: profile.address || null,
        })
        .eq("user_id", user.id);

      if (error) throw error;
      toast.success(language === "ar" ? "تم حفظ الملف الشخصي" : "Profile saved successfully");
      setEditing(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const totalSpent = orders.reduce((sum, o) => o.status !== "cancelled" ? sum + o.total_amount : sum, 0);
  const deliveredOrders = orders.filter((o) => o.status === "delivered").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="My Profile" description="Manage your Bait Al-Teeb profile and view order history." />
      <Navbar />
      <FloatingCart />
      <FloatingWhatsApp />

      <main className="pt-32 pb-20">
        <div className="container-luxury max-w-4xl">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <span className="text-primary text-sm tracking-[0.3em] uppercase">
              {language === "ar" ? "حسابي" : "My Account"}
            </span>
            <h1 className="text-5xl md:text-6xl font-serif mt-4 mb-4">
              {profile.full_name || (language === "ar" ? "الملف الشخصي" : "Profile")}
            </h1>
            {profile.created_at && (
              <p className="text-sm text-muted-foreground">
                {language === "ar" ? "عضو منذ" : "Member since"} {format(new Date(profile.created_at), "MMMM yyyy")}
              </p>
            )}
            <div className="divider-gold w-32 mx-auto mt-4" />
          </motion.div>

          {/* Account Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="grid grid-cols-3 gap-3 md:gap-4 mb-8">
            <div className="bg-card border border-border rounded-sm p-4 text-center">
              <ShoppingBag className="w-5 h-5 text-primary mx-auto mb-2" />
              <p className="text-2xl font-serif">{orders.length}</p>
              <p className="text-xs text-muted-foreground">{language === "ar" ? "إجمالي الطلبات" : "Total Orders"}</p>
            </div>
            <div className="bg-card border border-border rounded-sm p-4 text-center">
              <CreditCard className="w-5 h-5 text-primary mx-auto mb-2" />
              <p className="text-2xl font-serif">{totalSpent.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">{language === "ar" ? "إجمالي الإنفاق (ج.م)" : "Total Spent (EGP)"}</p>
            </div>
            <div className="bg-card border border-border rounded-sm p-4 text-center">
              <Package className="w-5 h-5 text-primary mx-auto mb-2" />
              <p className="text-2xl font-serif">{deliveredOrders}</p>
              <p className="text-xs text-muted-foreground">{language === "ar" ? "تم التوصيل" : "Delivered"}</p>
            </div>
          </motion.div>

          {/* Personal Information */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card border border-border rounded-sm p-6 md:p-8 mb-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-serif">{language === "ar" ? "المعلومات الشخصية" : "Personal Information"}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {language === "ar"
                      ? "هذه البيانات تُستخدم لملء معلومات الشحن تلقائياً عند الدفع"
                      : "This info auto-fills your shipping details at checkout"}
                  </p>
                </div>
              </div>
              {!editing && (
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="text-primary">
                  <Edit3 className="w-4 h-4 mr-1.5" />
                  {language === "ar" ? "تعديل" : "Edit"}
                </Button>
              )}
            </div>

            {editing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">{language === "ar" ? "الاسم الكامل" : "Full Name"}</label>
                    <Input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">{language === "ar" ? "البريد الإلكتروني" : "Email"}</label>
                    <Input value={user?.email || ""} disabled className="opacity-60" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" /> {language === "ar" ? "رقم الهاتف" : "Phone"}
                    </label>
                    <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" /> {language === "ar" ? "العنوان" : "Address"}
                    </label>
                    <Textarea value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} rows={2} />
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button variant="bronze" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {language === "ar" ? "حفظ التغييرات" : "Save Changes"}
                  </Button>
                  <Button variant="ghost" onClick={() => setEditing(false)}>
                    {language === "ar" ? "إلغاء" : "Cancel"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{language === "ar" ? "الاسم الكامل" : "Full Name"}</p>
                    <p className="text-foreground font-medium">{profile.full_name || <span className="text-muted-foreground italic">{language === "ar" ? "غير محدد" : "Not set"}</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Mail className="w-3 h-3" /> {language === "ar" ? "البريد الإلكتروني" : "Email"}</p>
                    <p className="text-foreground font-medium">{user?.email}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> {language === "ar" ? "رقم الهاتف" : "Phone"}</p>
                    <p className="text-foreground font-medium">{profile.phone || <span className="text-muted-foreground italic">{language === "ar" ? "غير محدد" : "Not set"}</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {language === "ar" ? "العنوان" : "Address"}</p>
                    <p className="text-foreground font-medium">{profile.address || <span className="text-muted-foreground italic">{language === "ar" ? "غير محدد" : "Not set"}</span>}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Order History */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-serif">{language === "ar" ? "سجل الطلبات" : "Order History"}</h2>
              <span className="text-sm text-muted-foreground ml-auto">{orders.length} {language === "ar" ? "طلب" : "orders"}</span>
            </div>

            {orders.length === 0 ? (
              <div className="bg-card border border-border rounded-sm p-8 text-center">
                <Package className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                <p className="text-muted-foreground">{language === "ar" ? "لا توجد طلبات بعد" : "No orders yet"}</p>
                <Button variant="bronze" className="mt-4" onClick={() => navigate("/collection")}>
                  {language === "ar" ? "تسوق الآن" : "Start Shopping"}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => {
                  const isExpanded = expandedOrder === order.id;
                  const statusClass = statusColors[order.status] || statusColors.pending;
                  return (
                    <div key={order.id} className="bg-card border border-border rounded-sm overflow-hidden">
                      <button
                        onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                        className="w-full flex items-center justify-between p-4 md:p-5 hover:bg-muted/20 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm text-muted-foreground">#{order.id.slice(0, 8)}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium tracking-wider uppercase ${statusClass}`}>
                                {order.status}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {format(new Date(order.created_at), "MMM d, yyyy")} · {order.total_amount.toFixed(2)} EGP
                            </p>
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </button>

                      {isExpanded && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="border-t border-border/50 px-4 md:px-5 py-4">
                          <div className="space-y-2">
                            {order.items.map((item, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-foreground">
                                  {item.product_name} {item.size && `(${item.size})`} × {item.quantity}
                                </span>
                                <span className="text-muted-foreground">{(item.unit_price * item.quantity).toFixed(2)} EGP</span>
                              </div>
                            ))}
                            {order.shipping_city && (
                              <div className="flex justify-between text-sm pt-2 border-t border-border/50">
                                <span className="text-muted-foreground">{language === "ar" ? "الشحن" : "Shipping"} ({order.shipping_city})</span>
                                <span className="text-muted-foreground">{order.shipping_fee} EGP</span>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Profile;
